import { useSeo } from "../hooks/useSeo";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";

const LAST_UPDATED = "2026. február";

export default function CookiePolicy() {
  useSeo({
    title: "Cookie-tájékoztató | Hair Elixir Hungary",
    description: "A Hair Elixir Hungary cookie-tájékoztatója.",
    path: "/cookie-tajekoztato",
  });
  const ref = useRef(null);
  useRevealAll(ref);

  return (
    <main
      ref={ref}
      data-testid="page-cookies"
      className="pt-28 md:pt-32 bg-[color:var(--he-bg)]"
    >
      {/* Breadcrumb */}
      <div className="he-container py-8 flex items-center justify-between">
        <Link to="/" className="btn-ghost" data-testid="cookies-back">
          <ChevronLeft size={14} className="ghost-arrow" />
          <span>Vissza a kezdőlapra</span>
        </Link>
        <span className="overline-xs text-[color:var(--he-ink-soft)] hidden sm:inline">
          Utolsó frissítés · {LAST_UPDATED}
        </span>
      </div>

      {/* Header */}
      <section className="he-container pt-8 pb-16 md:pb-24 border-b border-[color:var(--he-line)]">
        <p className="overline mb-5 mag-marker reveal">
          <span>Süti-tájékoztató</span>
        </p>
        <h1
          className="font-serif-display tracking-[-0.022em] reveal delay-1"
          style={{
            fontWeight: 400,
            fontSize: "clamp(2.6rem, 6vw, 5.2rem)",
            lineHeight: 1.05,
          }}
        >
          Cookie-<span className="italic text-[color:var(--he-gold)]">tájékoztató.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[color:var(--he-ink-soft)] font-light leading-[1.85] reveal delay-2">
          Röviden: mit tárolunk a böngésződben, miért, meddig, és hogyan
          tilthatod le. Reklámkövetést nem alkalmazunk; harmadik félnek nem
          adjuk el az adataidat.
        </p>
      </section>

      {/* Body */}
      <section className="he-container py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        <aside className="md:col-span-3 order-2 md:order-1">
          <div className="md:sticky md:top-32">
            <p className="overline-xs text-[color:var(--he-gold)] mb-5">Tartalomjegyzék</p>
            <ol className="space-y-3 text-sm font-light">
              {[
                ["#mi-a-cookie", "1. Mi az a cookie"],
                ["#milyen-cookiek", "2. Milyen cookie-kat használunk"],
                ["#harmadik-fel", "3. Harmadik felek"],
                ["#kezeles", "4. Beállítás és tiltás"],
                ["#jogalap", "5. Jogalap"],
                ["#modositas", "6. Módosítás"],
              ].map(([href, label]) => (
                <li key={href}>
                  <a href={href} className="he-link text-[color:var(--he-ink-soft)]">
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        <article className="md:col-span-9 order-1 md:order-2 space-y-14 reveal">
          <Section id="mi-a-cookie" title="1. Mi az a cookie">
            <p>
              A cookie (süti) egy kisméretű szöveges információ, amelyet a
              weboldal a böngésződbe ment. Segít, hogy a rendszer felismerje,
              amíg az oldalon vagy — például, hogy egy oldal elhagyása után
              is megőrizze a kosaradat, vagy ne kelljen minden kattintásra
              újra bejelentkezned.
            </p>
            <p>
              A süti nem képes programot futtatni, vírust bevinni vagy hozzá­
              férni a merevlemezed egyéb tartalmához. A böngésződben bármikor
              törölheted vagy letilthatod őket.
            </p>
          </Section>

          <Section id="milyen-cookiek" title="2. Milyen cookie-kat használ a Hair Elixir">
            <p className="mb-3">
              Csak feltétlenül szükséges és funkcionális tárolást alkalmazunk.
              Reklámkövető, hirdetést személyre szabó és analitikai
              harmadik-fél cookie-t <strong>nem használunk</strong> a saját
              domainünkön.
            </p>
            <CookieTable
              rows={[
                {
                  category: "Feltétlenül szükséges",
                  purpose: "A weboldal működéséhez elengedhetetlen — pl. munkamenet-azonosító, sütikonszen­t státuszának tárolása.",
                  storage: "cookieConsent, sessionId",
                  lifetime: "Munkamenet vagy 12 hónap",
                  type: "First-party",
                },
                {
                  category: "Funkcionális (localStorage)",
                  purpose: "A kosár tartalmának megőrzése, hogy oldalak között ne vesszen el a kiválasztásod. Böngésződben, saját eszközödön tárolódik.",
                  storage: "he_cart",
                  lifetime: "A böngészőadatok törléséig",
                  type: "First-party",
                },
                {
                  category: "Fizetés (Stripe)",
                  purpose: "Amikor a Fizetés gombra kattintasz, átirányítunk a Stripe biztonságos oldalára. A Stripe saját biztonsági és csalásmegelőzési célból cookie-kat helyez el a checkout.stripe.com domainen. Ezekre a mi weboldalunkról nincs ráhatásunk.",
                  storage: "Lásd a Stripe adatvédelmi tájékoztatóját",
                  lifetime: "Változó (a Stripe kezelésében)",
                  type: "Third-party",
                },
              ]}
            />
          </Section>

          <Section id="harmadik-fel" title="3. Harmadik felek szolgáltatásai">
            <p>
              A weboldal működéséhez az alábbi külső szolgáltatókat vesszük
              igénybe. Az általuk kezelt sütikről a saját tájékoztatóikban
              olvashatsz részletesen:
            </p>
            <Bullets
              items={[
                <>
                  <strong>Stripe Payments Europe, Ltd.</strong> — fizetési feldolgozás,
                  csalásmegelőzés. <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="he-link">stripe.com/privacy</a>
                </>,
                <>
                  <strong>Google (Gmail SMTP)</strong> — a rendelés-visszaigazoló e-mail
                  kézbesítése. Cookie-t a mi domainünkön nem helyez el.
                </>,
                <>
                  <strong>Emergent Agent, Inc.</strong> — hosting szolgáltató. Technikai
                  naplózás céljából rövid ideig őrzött IP-cím és user-agent
                  információ, cookie nélkül.
                </>,
              ]}
            />
          </Section>

          <Section id="kezeles" title="4. Cookie-k beállítása és tiltása">
            <p>
              A böngésződ beállításaiban bármikor megnézheted, letilthatod
              vagy törölheted az elhelyezett sütiket. Az alábbi linkeken
              találsz részletes útmutatót:
            </p>
            <Bullets
              items={[
                <a href="https://support.google.com/chrome/answer/95647" className="he-link" target="_blank" rel="noreferrer">Google Chrome</a>,
                <a href="https://support.mozilla.org/hu/kb/sutik-informacio-amelyet-weboldalak-tarolnak-szami" className="he-link" target="_blank" rel="noreferrer">Mozilla Firefox</a>,
                <a href="https://support.apple.com/hu-hu/guide/safari/sfri11471/mac" className="he-link" target="_blank" rel="noreferrer">Safari (Mac)</a>,
                <a href="https://support.microsoft.com/hu-hu/microsoft-edge" className="he-link" target="_blank" rel="noreferrer">Microsoft Edge</a>,
              ]}
            />
            <p className="mt-6">
              Fontos: a feltétlenül szükséges cookie-k letiltása esetén
              előfordulhat, hogy egyes funkciók — pl. a kosár — nem működnek
              helyesen.
            </p>
          </Section>

          <Section id="jogalap" title="5. Az adatkezelés jogalapja">
            <Bullets
              items={[
                "Feltétlenül szükséges cookie-k: az elektronikus hírközlési törvény (Eht.) 155. § alapján — hozzájárulás nem szükséges, mert kizárólag a szolgáltatás nyújtásához elengedhetetlenek.",
                "Egyéb, funkcionális célú tárolás (pl. kosár): az érintett előzetes, önkéntes hozzájárulása — GDPR 6. cikk (1) a).",
                "A hozzájárulás bármikor visszavonható a böngésző-beállítások megváltoztatásával.",
              ]}
            />
            <p className="mt-6">
              A cookie-k által kezelt adatok részletes felhasználásáról az{" "}
              <Link to="/adatvedelem" className="he-link" data-testid="cookies-to-privacy">
                Adatvédelmi tájékoztatónkban
              </Link>{" "}
              olvashatsz.
            </p>
          </Section>

          <Section id="modositas" title="6. A tájékoztató módosítása">
            <p>
              Ha bővül a szolgáltatásunk vagy változnak a beépülő partnerek,
              ezt a tájékoztatót frissítjük. A módosított változat a
              közzététel napjától hatályos.
            </p>
            <p className="mt-6 text-[color:var(--he-ink-soft)] font-light">
              Jelen dokumentum hatályos: <strong>{LAST_UPDATED}</strong>.
            </p>
          </Section>

          <div className="pt-10 border-t border-[color:var(--he-line)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <Link to="/adatvedelem" className="btn-ghost" data-testid="cookies-to-privacy-cta">
              <span>Adatvédelmi tájékoztató</span>
              <ArrowUpRight size={14} className="ghost-arrow" />
            </Link>
            <Link to="/kapcsolat" className="btn-outline-gold" data-testid="cookies-contact">
              <span>Kérdésed van? Írj nekünk</span>
            </Link>
          </div>
        </article>
      </section>
    </main>
  );
}

/* ---------- helpers (identical shape to PrivacyPolicy for consistency) ---------- */

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-32 reveal">
      <h2
        className="font-serif-display tracking-[-0.018em] mb-6"
        style={{ fontWeight: 400, fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", lineHeight: 1.15 }}
      >
        {title}
      </h2>
      <div className="space-y-4 text-[color:var(--he-ink)] font-light leading-[1.85]">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2.5 mt-3">
      {items.map((it, i) => (
        <li key={i} className="pl-5 relative">
          <span
            className="absolute left-0 top-[0.85em] w-2 h-px bg-[color:var(--he-gold)]"
            aria-hidden
          />
          {it}
        </li>
      ))}
    </ul>
  );
}

function CookieTable({ rows }) {
  return (
    <div className="mt-5 space-y-6">
      {rows.map((r, i) => (
        <div
          key={i}
          className="border border-[color:var(--he-line)] p-6 md:p-8 hover:border-[color:var(--he-gold)]/50 transition-colors duration-500"
        >
          <div className="flex items-center justify-between gap-4 mb-4">
            <p className="overline-xs text-[color:var(--he-gold)]">{r.category}</p>
            <span className="overline-xs text-[color:var(--he-ink-soft)]">{r.type}</span>
          </div>
          <p className="text-[color:var(--he-ink)] font-light leading-[1.75] mb-5">
            {r.purpose}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pt-4 border-t border-[color:var(--he-line)]">
            <div>
              <p className="overline-xs text-[color:var(--he-ink-soft)] mb-1">Tárolt kulcs</p>
              <p className="text-sm font-light break-all">{r.storage}</p>
            </div>
            <div>
              <p className="overline-xs text-[color:var(--he-ink-soft)] mb-1">Élettartam</p>
              <p className="text-sm font-light">{r.lifetime}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
